import React, {useCallback, useEffect} from "react";

import './UsersTable.scss'
import {useRecoilState, useRecoilValue} from "recoil";
import {
  deleteModalIdState, deleteModalOpenState, errorState, loadingState, rowsState
} from "../../state/users_table";
import {grpcUserServiceSelector} from "../../state/grpc";
import {RowDelete} from '@carbon/react/icons';
import {
  Button,
  DataTable,
  DataTableSkeleton,
  Modal,
  OverflowMenu,
  OverflowMenuItem,
  Table,
  TableBatchAction,
  TableBatchActions,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeader,
  TableRow,
  TableSelectAll,
  TableSelectRow,
  TableToolbar,
  TableToolbarContent,
  TableToolbarSearch
} from "@carbon/react";
import {useNavigate} from "react-router-dom";
import {ADD, useToastContext} from "../Toast"

const UsersTable = () => {
  const headers = [{
    key: 'id', header: 'ID'
  }, {
    key: 'username', header: 'Username'
  }, {
    key: 'email', header: 'Email'
  }, {
    key: 'firstname', header: 'Nome'
  }, {
    key: 'lastname', header: 'Cognome'
  }, {
    key: 'company', header: 'Azienda'
  }, {
    key: 'role', header: 'Ruolo'
  }, {
    key: 'enabled', header: 'Attivo'
  }];

  const { toastDispatch } = useToastContext();
  const navigate = useNavigate();
  const [loading, setLoading] = useRecoilState(loadingState)
  const [error, setError] = useRecoilState(errorState)
  const [rows, setRows] = useRecoilState(rowsState)
  const [deleteModalOpen, setDeleteModalOpen] = useRecoilState(deleteModalOpenState)
  const [deleteModalId, setDeleteModalId] = useRecoilState(deleteModalIdState)
  const usersService = useRecoilValue(grpcUserServiceSelector)

  const navigateToAdd = useCallback(() => {
    navigate('/users/add')
  }, [navigate])

  const navigateToEdit = useCallback((username) => {
    navigate('/users/' + username)
  }, [navigate])

  async function getUsersTableData() {
    const req = new proto.api.ListUsersRequest();
    usersService.listUsers(req, null, (err, resp) => {
      if (err != null) {
        setError(true)
      } else {
        setRows(resp.toObject().usersList)
        setLoading(false)
      }
    })
  }

  const handleUserEnable = (userId, isEnabled) => {
    if (isEnabled) {
      const req = new proto.api.EnableUserRequest()
      req.setId(userId)
      usersService.enableUser(req, null, (err, resp) => {
        if (err != null) {
          console.error(err)
          toastDispatch({
            type: ADD,
            payload: {
              title: "Operazione fallita",
              content: "Impossibile abilitare l'utente",
              kind: "error",
              caption: err.message
            }
          })
        } else {
          getUsersTableData()
          toastDispatch({
            type: ADD,
            payload: {
              title: "Operazione avvenuta con successo",
              content: "Utente abilitato con successo",
              kind: "success"
            }
          })
        }
      })
    } else {
      const req = new proto.api.DisableUserRequest()
      req.setId(userId)
      usersService.disableUser(req, null, (err, resp) => {
        if (err != null) {
          console.error(err)
          toastDispatch({
            type: ADD,
            payload: {
              title: "Operazione fallita",
              content: "Impossibile disabilitare l'utente",
              kind: "error",
              caption: err.message
            }
          })
        } else {
          getUsersTableData()
          toastDispatch({
            type: ADD,
            payload: {
              title: "Operazione avvenuta con successo",
              content: "Utente disabilitato con successo",
              kind: "success"
            }
          })
        }
      })
    }
  }

  useEffect(() => {
    getUsersTableData();
    // eslint-disable-next-line
  }, [usersService, setError, setLoading, setRows]);

  if (loading) {
    return (<DataTableSkeleton
        columnCount={headers.length + 1}
        rowCount={5}
        headers={headers}
      />)
  }

  if (error) {
    return (<div>
        <span>ERROR!</span>
      </div>)
  }

  const processChildren = (cell) => {
    if (cell.info.header === 'enabled') {
      return (
        <div title={cell.value === "true" ? "Attivo" : "Disattivo"} className={cell.value === "true" ? "light green" : "light red"} />
      )
    }
    return (
      <>
        {cell.value}
      </>
    )
  }

  return (<DataTable
    rows={rows}
    headers={headers}
    isSortable
    render={({
               rows,
               headers,
               getHeaderProps,
               getRowProps,
               getSelectionProps,
               getToolbarProps,
               getBatchActionProps,
               onInputChange,
               selectedRows,
               getTableProps,
               getTableContainerProps,
             }) => {
      return (
        <TableContainer title="Lista operatori" description="Lista degli operatori abilitati" {...getTableContainerProps()}>
          <Modal
            open={deleteModalOpen}
            danger
            modalHeading={"Sei sicuro di voler eliminare l'utente " + deleteModalId + " ?"}
            modalLabel="Elimina utente"
            primaryButtonText="Elimina"
            onRequestSubmit={() => {
              const req = new proto.api.DeleteUserRequest()
              req.setId(deleteModalId)
              usersService.deleteUser(req, null, (err, resp) => {
                if (err != null) {
                  console.error(err)
                } else {
                  getUsersTableData()
                  setDeleteModalOpen(false)
                }
              })
            }}
            onRequestClose={() => {
              setDeleteModalOpen(false)
            }}
            secondaryButtonText="Annulla"
          />
          <TableToolbar {...getToolbarProps()} >
            <TableBatchActions {...getBatchActionProps()}>
              <TableBatchAction
                renderIcon={RowDelete}
                iconDescription="Elimina le righe selezionate"
                onClick={() => console.log("batch action on: ", selectedRows.length, " rows")}>
                Elimina
              </TableBatchAction>
            </TableBatchActions>
            <TableToolbarContent>
              <TableToolbarSearch onChange={onInputChange} size="lg"/>
              <Button onClick={navigateToAdd}>Nuovo utente</Button>
            </TableToolbarContent>
          </TableToolbar>
          <Table {...getTableProps()}>
            <TableHead>
              <TableRow>
                <TableSelectAll {...getSelectionProps()} />
                {headers.map((header) => (<TableHeader {...getHeaderProps({header})}>
                  {header.header}
                </TableHeader>))}
                <TableHeader style={{width: '1rem'}} scope="col"/>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (<TableRow key={row.id} {...getRowProps({row})}>
                <TableSelectRow {...getSelectionProps({row})} />
                {row.cells.map((cell) => (<TableCell key={cell.id}>
                    {processChildren(cell)}
                  </TableCell>))}
                <TableCell className="cds--table-column-menu">
                  <OverflowMenu flipped size='md' aria-label="">
                    <OverflowMenuItem itemText="Modifica" onClick={() => navigateToEdit(row.id)}/>
                    <OverflowMenuItem itemText="Attiva" onClick={() => handleUserEnable(row.id, true)} />
                    <OverflowMenuItem itemText="Disattiva" onClick={() => handleUserEnable(row.id, false)} />
                    <OverflowMenuItem isDelete itemText="Elimina"
                                      onClick={() => {
                                        setDeleteModalId(row.id)
                                        setDeleteModalOpen(true)
                                      }}/>
                  </OverflowMenu>
                </TableCell>
              </TableRow>))}
            </TableBody>
          </Table>
        </TableContainer>);
    }}
  />)
}

export default UsersTable